import type { DragConstraint, DragUpdateResult } from "@project/emerge-types";
import type * as THREE from "three";
import { InteriorType } from "../../enum/InteriorType";
import type { CaseInteriorOptions } from "../../interfaces/AccessoryCompartmentOptions";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import {
  calculatePanelsAndVolumes,
  getToleranceWithFallback,
  getTrayMaterialWithFallback,
} from "../interior-helpers/InteriorHelpers";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

const MIN_SIZE = 50;
const MAX_SIZE = 5000;

function buildDisplayValues(
  mode: "xz" | "y",
  newWidth: number,
  newHeight: number,
  newDepth: number,
) {
  return {
    primary: {
      label: mode === "xz" ? "W×D" : "H",
      value: mode === "xz" ? newWidth : newHeight,
      unit: mode === "xz" ? `×${newDepth}mm` : "mm",
    },
  };
}

/**
 * Drag handler for CASE interior type.
 * Only enabled for single-item interiors (1 volume, 0 panels).
 */
export const CaseCornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings, buildstepName: string): boolean {
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return false;

    const { tolerance, trayMaterial } = interior;
    const finalTrayMaterial = getTrayMaterialWithFallback(
      trayMaterial,
      buildstepName,
    );
    if (!finalTrayMaterial) return false;

    const finalTolerance = getToleranceWithFallback(tolerance, buildstepName);

    const result = calculatePanelsAndVolumes(
      interior,
      finalTrayMaterial,
      finalTolerance,
      buildstepName,
    );
    if (!result) return false;

    return result.volumes.length === 1 && result.panels.length === 0;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const isBottom = caseCornerPosition.endsWith("Bottom");
    const isLeft = caseCornerPosition.startsWith("left");
    const isFront = caseCornerPosition.includes("Front");

    const interior = settings.interiorOptions as CaseInteriorOptions;
    const tolerance = interior.tolerance ?? { x: 7, y: 2, z: 7 };

    let currentWidth = innerDimensions.x - 2 * tolerance.x;
    let currentHeight = innerDimensions.y - tolerance.y;
    let currentDepth = innerDimensions.z - 2 * tolerance.z;

    // Try to get actual dimensions from tree or contents
    const { tree, contents } = interior;
    if (tree?.children?.[0]) {
      const firstChild = tree.children[0];
      if ("item" in firstChild && firstChild.item) {
        const item = firstChild.item;
        currentWidth = item.width ?? currentWidth;
        currentHeight = item.height ?? currentHeight;
        currentDepth = item.depth ?? currentDepth;
      }
    } else if (contents?.[0]) {
      currentWidth = contents[0].width ?? currentWidth;
      currentHeight = contents[0].height ?? currentHeight;
      currentDepth = contents[0].depth ?? currentDepth;
    }

    const xSign = isLeft ? -1 : 1;
    const zSign = isFront ? -1 : 1;

    if (isBottom) {
      // Bottom corners: resize x and z on horizontal plane
      const halfMinX = MIN_SIZE / 2;
      const halfMaxX = MAX_SIZE / 2;
      const halfMinZ = MIN_SIZE / 2;
      const halfMaxZ = MAX_SIZE / 2;

      const minX = xSign * halfMinX;
      const maxX = xSign * halfMaxX;
      const minZ = zSign * halfMinZ;
      const maxZ = zSign * halfMaxZ;

      const dragConstraint: DragConstraint = {
        type: "surface",
        planeNormal: [0, 1, 0],
        bounds: {
          min: [Math.min(minX, maxX), position.y, Math.min(minZ, maxZ)],
          max: [Math.max(minX, maxX), position.y, Math.max(minZ, maxZ)],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_xz",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "xz",
          interiorType: "case",
          xSign,
          zSign,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    } else {
      // Top corners: resize height along vertical axis
      const bottomY = -innerDimensions.y / 2;
      const minY = bottomY + MIN_SIZE;
      const maxY = bottomY + MAX_SIZE;

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 1, 0],
        bounds: {
          min: [position.x, minY, position.z],
          max: [position.x, maxY, position.z],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_y",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "y",
          interiorType: "case",
          bottomY,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    }
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "xz" | "y";
    const currentWidth = settingsContext.currentWidth as number;
    const currentHeight = settingsContext.currentHeight as number;
    const currentDepth = settingsContext.currentDepth as number;

    const newWidth =
      mode === "xz"
        ? Math.max(MIN_SIZE, Math.round(Math.abs(newPosition.x) * 2))
        : currentWidth;
    const newDepth =
      mode === "xz"
        ? Math.max(MIN_SIZE, Math.round(Math.abs(newPosition.z) * 2))
        : currentDepth;
    const newHeight =
      mode === "y"
        ? Math.max(
            MIN_SIZE,
            Math.round(newPosition.y - (settingsContext.bottomY as number)),
          )
        : currentHeight;

    const interior = currentSettings.interiorOptions as CaseInteriorOptions;
    const { tree, contents } = interior;
    const displayValues = buildDisplayValues(
      mode,
      newWidth,
      newHeight,
      newDepth,
    );

    if (tree?.children?.length) {
      const firstChild = tree.children[0];
      if (firstChild === undefined) return null;
      if ("item" in firstChild && firstChild.item) {
        const updatedTree = {
          ...tree,
          children: [
            {
              ...firstChild,
              item: {
                ...firstChild.item,
                width: newWidth,
                height: newHeight,
                depth: newDepth,
              },
            },
            ...tree.children.slice(1),
          ],
        };
        return {
          settingsUpdate: {
            interiorOptions: { ...interior, tree: updatedTree },
          },
          displayValues,
        };
      }
    }

    if (contents?.length) {
      const firstContent = contents[0];
      if (firstContent === undefined) return null;
      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            contents: [
              {
                ...firstContent,
                width: newWidth,
                height: newHeight,
                depth: newDepth,
              },
              ...contents.slice(1),
            ],
          },
        },
        displayValues,
      };
    }

    return null;
  },
};
